# Design Guidelines

AI App Design components live inside chat messages. This page covers the constraints and patterns that make them work.

## The collaboration model

Every tool UI involves three participants: the **assistant**, the **component**, and the **user**.

The assistant introduces the component, the user scans it and interacts, and the assistant responds by referencing specific elements. This triadic loop is fundamental to how conversation-native UIs work.

## Roles

Every component has a primary role:

- **Information** -- Display data. Users read more than they click. Examples: Data Table, Link Preview, Item Carousel.
- **Decision** -- For choices that matter. Show clear options and receipts that confirm what happened. Examples: Approval Card, Option List.
- **Control** -- Adjust parameters without commitment. Changes are reversible. Examples: sliders, filters, preferences panels.
- **State** -- Show internal activity like progress indicators and status logs.

Some components combine roles. A data table with row actions is "information with control." When combining, pick one role to lead.

## Constraints

Tool UIs live in chat: narrow widths, variable heights, mixed with prose.

- **Width**: Components should work at 320px and scale gracefully to ~700px. Never assume full viewport width.
- **Height**: Keep it compact. The user should see the complete surface without scrolling when possible.
- **Scroll**: The chat message list scrolls vertically. Avoid inner scroll containers unless absolutely necessary.
- **Motion**: Respect `prefers-reduced-motion`. Transitions should be subtle and functional, not decorative.

## Addressability

If the assistant can't point at something later, you lose half the value of rendering it.

Use stable IDs from your backend -- database IDs, canonical URLs -- not array indexes or render-time UUIDs. Anything the user can act on should have an ID the assistant can cite.

## Anti-patterns

- Embedding full pages inside a chat bubble
- Components that require horizontal scrolling to read primary content
- Auto-playing media without user consent
- Modals or overlays that break the chat scroll context
- Components that mutate state silently without producing a receipt
